import { SemanticICONS } from 'semantic-ui-react';

/**
 * `compliance.giftAidStatus` on the organisation record, as served by
 * `api/v2/organization/{id}`. Enums come off the wire camelCase-first-letter.
 *
 * The record is already loaded into the template state by the time the finance
 * page renders, so this is what the panel shows on arrival. The refresh endpoint
 * is never called just to populate it.
 */
export const giftAidStatus = {
  notEligible: 'notEligible',
  notLinked: 'notLinked',
  linked: 'linked'
} as const;

export type GiftAidStatus = (typeof giftAidStatus)[keyof typeof giftAidStatus];

/**
 * What the panel currently knows. `unknown` covers an empty or unrecognised
 * `giftAidStatus`: better to say nothing than to assert a state the record does
 * not support.
 */
export const swiftaidLinkState = {
  unknown: 'unknown',
  ...giftAidStatus
} as const;

export type SwiftaidLinkState =
  (typeof swiftaidLinkState)[keyof typeof swiftaidLinkState];

const knownStates = new Set<string>(Object.values(giftAidStatus));

/** Map a raw `giftAidStatus` onto a panel state, tolerating null and "". */
export const readGiftAidStatus = (value: unknown): SwiftaidLinkState =>
  typeof value === 'string' && knownStates.has(value)
    ? (value as SwiftaidLinkState)
    : swiftaidLinkState.unknown;

/** i18n key suffix for each state, under the `swiftaid` section. */
export const swiftaidLinkStateI18nKey: Record<SwiftaidLinkState, string> = {
  [swiftaidLinkState.unknown]: 'not-checked',
  [swiftaidLinkState.notEligible]: 'not-eligible',
  [swiftaidLinkState.notLinked]: 'not-connected',
  [swiftaidLinkState.linked]: 'connected'
};

/** Semantic UI icon for each state. State is never carried by colour alone. */
export const swiftaidLinkStateIcon: Record<SwiftaidLinkState, SemanticICONS> = {
  [swiftaidLinkState.unknown]: 'question circle outline',
  [swiftaidLinkState.notEligible]: 'ban',
  [swiftaidLinkState.notLinked]: 'circle outline',
  [swiftaidLinkState.linked]: 'check circle'
};

/**
 * Path to the status on the organisation template state. Kept here so the panel
 * and its tests agree on where the value comes from.
 */
export const giftAidStatusPath = 'compliance.giftAidStatus';

/**
 * Path to the HMRC charity reference on the organisation template state. A UK
 * organisation without one is `notEligible`, which the panel explains. Defined here
 * rather than in the organisation constants because this module is imported by the
 * app-wide org info cache and must stay free of the config those constants pull in.
 */
export const hmrcCharityReferencePath = 'compliance.hmrcCharityReference';

/**
 * Where the connect button is in its two-step flow. `checking` is the refresh
 * call that decides whether the registration form needs opening at all,
 * `starting` is the start call that produces the form URL.
 */
export const connectPhase = {
  idle: 'idle',
  checking: 'checking',
  starting: 'starting'
} as const;

export type ConnectPhase = (typeof connectPhase)[keyof typeof connectPhase];
